'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<string>('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqItems: FAQItem[] = [
    {
      id: '1',
      question: 'Czy konsultacja jest naprawdę bezpłatna?',
      answer: 'Tak, pierwsza konsultacja jest całkowicie bezpłatna i bez zobowiązań. Zarabiamy prowizję od banków po pomyślnym sfinalizowaniu transakcji, więc nie ponosisz żadnych kosztów z naszej strony.',
    },
    {
      id: '2',
      question: 'Jak długo trwa cały proces kredytu hipotecznego?',
      answer: 'Gwarantujemy przedstawienie najlepszej oferty kredytu hipotecznego w ciągu 48 godzin od pierwszej konsultacji. Cały proces od aplikacji do wypłaty środków zazwyczaj trwa 2-4 tygodnie, w zależności od banku.',
    },
    {
      id: '3',
      question: 'Czy współpraca z doradcą wpływa na moją zdolność kredytową?',
      answer: 'Nie, wręcz przeciwnie. Dzięki naszemu doświadczeniu przygotowujemy dokumenty w sposób, który maksymalizuje Twoją zdolność kredytową. Dodatkowo, zamiast składać wnioski do wielu banków samodzielnie, my robimy to za Ciebie, co nie wpływa negatywnie na Twoją historię kredytową.',
    },
    {
      id: '4',
      question: 'Jakie dokumenty będę potrzebował do kredytu hipotecznego?',
      answer: 'Podstawowe dokumenty to dowód osobisty, zaświadczenie o dochodach i wyciągi bankowe. Dokładną listę dokumentów dostosowaną do Twojej sytuacji przedstawimy podczas konsultacji. Pomożemy Ci również w ich przygotowaniu.',
    },
    {
      id: '5',
      question: 'Czy mogę otrzymać kredyt hipoteczny z niską zdolnością kredytową?',
      answer: 'Tak, współpracujemy z wieloma bankami, które mają różne kryteria oceny. Nawet jeśli jeden bank odmówi, możemy znaleźć alternatywne rozwiązanie. Naszym celem jest znalezienie najlepszej opcji dla Twojej sytuacji.',
    },
    {
      id: '6',
      question: 'Czy mogę zrezygnować z usługi w dowolnym momencie?',
      answer: 'Tak, możesz zrezygnować w dowolnym momencie bez żadnych konsekwencji finansowych. Nie pobieramy żadnych opłat z góry, więc nie ponosisz ryzyka finansowego.',
    },
    {
      id: '7',
      question: 'Jak mogę być pewien, że otrzymam najlepszą ofertę kredytu hipotecznego?',
      answer: 'Współpracujemy z 20+ bankami i porównujemy wszystkie dostępne oferty kredytów hipotecznych. Dzięki naszemu doświadczeniu i relacjom z bankami często negocjujemy lepsze warunki niż te dostępne publicznie. Dodatkowo, przedstawiamy Ci pełne porównanie ofert, abyś mógł podjąć świadomą decyzję.',
    },
    {
      id: '8',
      question: 'Co jeśli mam już ofertę kredytu hipotecznego z banku?',
      answer: 'Świetnie! Możemy spróbować wynegocjować lepsze warunki lub znaleźć korzystniejszą ofertę w innym banku. Często udaje nam się obniżyć oprocentowanie lub poprawić inne warunki kredytu hipotecznego.',
    },
  ];

  const toggleFAQ = (id: string) => {
    setOpenFAQ(openFAQ === id ? '' : id);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
              Najczęściej zadawane pytania
            </h2>
          </div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-card rounded-xl h-20 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <Icon name="QuestionMarkCircleIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-body font-medium text-primary">
              FAQ
            </span>
          </div>

          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Najczęściej zadawane pytania
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Znajdź odpowiedzi na najczęstsze pytania dotyczące naszych usług
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-4">
          {faqItems.map(item => (
            <div
              key={item.id}
              className="bg-card rounded-xl shadow-card overflow-hidden transition-all duration-250"
            >
              <button
                onClick={() => toggleFAQ(item.id)}
                className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors duration-250 hover:bg-muted/30"
              >
                <span className="text-base font-body font-semibold text-foreground pr-4">
                  {item.question}
                </span>
                <Icon
                  name={openFAQ === item.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  variant="outline"
                  className="text-primary flex-shrink-0"
                />
              </button>

              {openFAQ === item.id && (
                <div className="px-6 pb-5 animate-fade-in">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-base text-muted-foreground mb-6">
            Nie znalazłeś odpowiedzi na swoje pytanie?
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                const offsetPosition = contactSection.offsetTop - 80;
                window.scrollTo({
                  top: offsetPosition,
                  behavior: 'smooth',
                });
              }
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground font-body font-semibold rounded-lg shadow-cta transition-all duration-250 hover:bg-accent/90 hover:shadow-lg"
          >
            <span>Skontaktuj się z nami</span>
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;